// Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.
// SPDX-License-Identifier: Apache-2.0
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import clsx from 'clsx';

import { useMergeRefs } from '@amzn/awsui-component-toolkit/internal';
import { getAnalyticsMetadataAttribute } from '@amzn/awsui-component-toolkit/internal/analytics-metadata';

import { InternalButton } from '../button/internal';
import { useInternalI18n } from '../i18n/context';
import { IconProps } from '../icon/interfaces';
import InternalIcon from '../icon/internal';
import { DATA_ATTR_ANALYTICS_ALERT } from '../internal/analytics/selectors';
import { getBaseProps } from '../internal/base-component';
import VisualContext from '../internal/components/visual-context';
import { LinkDefaultVariantContext } from '../internal/context/link-default-variant-context';
import { fireNonCancelableEvent } from '../internal/events';
import { InternalBaseComponentProps } from '../internal/hooks/use-base-component';
import { useVisualRefresh } from '../internal/hooks/use-visual-mode';
import { persistAlertDismiss, retrieveAlertDismiss } from '../internal/persistence';
import { awsuiPluginsInternal } from '../internal/plugins/api';
import { createUseDiscoveredAction, createUseDiscoveredContent } from '../internal/plugins/helpers';
import { SomeRequired } from '../internal/types';
import useContainerWidth from '../internal/utils/use-container-width';
import { ActionsWrapper } from './actions-wrapper';
import { GeneratedAnalyticsMetadataAlertDismiss } from './analytics-metadata/interfaces';
import { AlertProps } from './interfaces';
import { getAlertStyles, getDismissButtonStyles, getIconStyles } from './style';

import analyticsSelectors from './analytics-metadata/styles.css.js';
import styles from './styles.css.js';

const typeToIcon: Record<AlertProps.Type, IconProps['name']> = {
  error: 'status-negative',
  warning: 'status-warning',
  success: 'status-positive',
  info: 'status-info',
};

type InternalAlertProps = SomeRequired<AlertProps, 'type'> &
  InternalBaseComponentProps & {
    messageSlotId?: string;
    style?: AlertProps['style'];
    styleClassNames?: StyleClassNames;
  };

// Style API v2
interface StyleClassNames {
  root?: string;
  icon?: string;
  dismissButton?: string;
}

const useDiscoveredAction = createUseDiscoveredAction(awsuiPluginsInternal.alert.onActionRegistered);
const useDiscoveredContent = createUseDiscoveredContent('alert', awsuiPluginsInternal.alertContent);

const InternalAlert = React.forwardRef(
  (
    {
      type,
      i18nStrings,
      visible = true,
      dismissible,
      children,
      header,
      buttonText,
      action,
      onDismiss,
      onButtonClick,
      __internalRootRef,
      statusIconAriaLabel: deprecatedStatusIconAriaLabel,
      dismissAriaLabel: deprecatedDismissAriaLabel,
      messageSlotId,
      style,
      persistenceConfig,
      styleClassNames,
      ...rest
    }: InternalAlertProps,
    ref: React.Ref<AlertProps.Ref>
  ) => {
    const baseProps = getBaseProps(rest);
    const i18n = useInternalI18n('alert');

    const focusRef = useRef<HTMLDivElement>(null);
    useImperativeHandle(ref, () => ({
      focus: () => {
        if (focusRef.current) {
          focusRef.current.tabIndex = -1;
          focusRef.current.focus();
        }
      },
    }));
    const handleBlur = () => {
      if (focusRef.current) {
        focusRef.current.removeAttribute('tabindex');
      }
    };

    const { discoveredActions, headerRef: headerRefAction, contentRef: contentRefAction } = useDiscoveredAction(type);
    const {
      initialHidden,
      headerReplacementType,
      contentReplacementType,
      headerRef: headerRefContent,
      contentRef: contentRefContent,
      replacementHeaderRef,
      replacementContentRef,
    } = useDiscoveredContent({ type, header, children });

    const [containerWidth, containerMeasureRef] = useContainerWidth();
    const containerRef = useMergeRefs(containerMeasureRef, __internalRootRef);
    const headerRef = useMergeRefs(headerRefAction, headerRefContent);
    const contentRef = useMergeRefs(contentRefAction, contentRefContent);
    const [isPersistentlyDismissed, setIsPersistentlyDismissed] = useState(
      !!(persistenceConfig && persistenceConfig.uniqueKey)
    );

    const isRefresh = useVisualRefresh();
    const size = isRefresh
      ? 'normal'
      : headerReplacementType !== 'remove' && header && contentReplacementType !== 'remove' && children
        ? 'big'
        : 'normal';

    const hasAction = Boolean(action || buttonText || discoveredActions.length);

    const analyticsAttributes = {
      [DATA_ATTR_ANALYTICS_ALERT]: type,
    };

    const statusIconAriaLabel = i18n(
      `i18nStrings.${type}IconAriaLabel`,
      i18nStrings?.[`${type}IconAriaLabel`] ?? deprecatedStatusIconAriaLabel
    );

    const dismissAriaLabel = i18n(
      'i18nStrings.dismissAriaLabel',
      i18nStrings?.dismissAriaLabel ?? i18n('dismissAriaLabel', deprecatedDismissAriaLabel)
    );

    useEffect(() => {
      let isMounted = true;
      if (persistenceConfig?.uniqueKey) {
        retrieveAlertDismiss(persistenceConfig)
          .then(dismissed => {
            if (isMounted) {
              setIsPersistentlyDismissed(!!dismissed);
            }
          })
          .catch(() => {
            if (isMounted) {
              setIsPersistentlyDismissed(false);
            }
          });
      }
      return () => {
        isMounted = false;
      };

      // Only track specific properties to avoid re-running when persistenceConfig object reference changes
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [persistenceConfig?.uniqueKey, persistenceConfig?.crossServicePersistence]);

    const dismiss = () => {
      fireNonCancelableEvent(onDismiss);
      if (persistenceConfig?.uniqueKey) {
        persistAlertDismiss(persistenceConfig);
      }
    };

    if (isPersistentlyDismissed) {
      return null;
    }

    return (
      <div
        {...baseProps}
        {...analyticsAttributes}
        aria-hidden={!visible}
        className={clsx(
          styles.root,
          { [styles.hidden]: !visible, [styles['initial-hidden']]: initialHidden },
          baseProps.className
        )}
        ref={containerRef}
      >
        <LinkDefaultVariantContext.Provider value={{ defaultVariant: 'primary' }}>
          <VisualContext contextName="alert">
            <div
              className={clsx(
                styleClassNames?.root,
                styles.alert,
                styles[`type-${type}`],
                styles[`icon-size-${size}`],
                hasAction && styles['with-action'],
                dismissible && styles['with-dismiss']
              )}
              style={getAlertStyles(style)}
            >
              <div className={styles['alert-wrapper']}>
                <div className={styles['alert-focus-wrapper']} ref={focusRef} role="group" onBlur={handleBlur}>
                  <div className={clsx(styleClassNames?.icon, styles.icon, styles.text)} style={getIconStyles(style)}>
                    <InternalIcon name={typeToIcon[type]} size={size} ariaLabel={statusIconAriaLabel} />
                  </div>
                  <div className={clsx(styles.message, styles.text)} id={messageSlotId}>
                    <div
                      className={clsx(
                        header && styles.header,
                        headerReplacementType !== 'original' ? styles.hidden : analyticsSelectors.header
                      )}
                      ref={headerRef}
                    >
                      {header}
                    </div>
                    <div
                      className={clsx(
                        styles['header-replacement'],
                        headerReplacementType !== 'replaced' ? styles.hidden : analyticsSelectors.header
                      )}
                      ref={replacementHeaderRef}
                    ></div>
                    <div
                      className={clsx(styles.content, contentReplacementType !== 'original' && styles.hidden)}
                      ref={contentRef}
                    >
                      {children}
                    </div>
                    <div
                      className={clsx(
                        styles['content-replacement'],
                        contentReplacementType !== 'replaced' && styles.hidden
                      )}
                      ref={replacementContentRef}
                    ></div>
                  </div>
                </div>
                <ActionsWrapper
                  className={styles.action}
                  testUtilClasses={{
                    actionSlot: styles['action-slot'],
                    actionButton: styles['action-button'],
                  }}
                  action={action}
                  discoveredActions={discoveredActions}
                  buttonText={buttonText}
                  onButtonClick={() => fireNonCancelableEvent(onButtonClick)}
                  containerWidth={containerWidth}
                  wrappedClass={styles['action-wrapped']}
                />
              </div>
              {dismissible && (
                <div
                  className={styles.dismiss}
                  {...getAnalyticsMetadataAttribute({
                    action: 'dismiss',
                  } as Partial<GeneratedAnalyticsMetadataAlertDismiss>)}
                >
                  <InternalButton
                    className={styles['dismiss-button']}
                    variant="icon"
                    iconName="close"
                    formAction="none"
                    ariaLabel={dismissAriaLabel}
                    onClick={dismiss}
                    style={getDismissButtonStyles(style)}
                    styleClassNames={{ root: styleClassNames?.dismissButton }}
                  />
                </div>
              )}
            </div>
          </VisualContext>
        </LinkDefaultVariantContext.Provider>
      </div>
    );
  }
);

export default InternalAlert;



// WEBPACK FOOTER //
// /Volumes/brazil-workspaces/Phoenix/PITLambda/src/alert/internal.tsx